<template>
    <Head :title="t('chat.pastConversations.pageTitle')" />
    <BaseLayout gap="sm">
        <!-- Header -->
        <BasePageHeader :title="t('chat.pastConversations.pageTitle')">
            <template #subtitle>
                <span :class="{ 'md:block hidden': searchPanelHasSearched }">
                    {{ t("chat.pastConversations.subtitleCount", { count: totalCount }, totalCount) }}
                    <span class="hidden md:inline">{{ t("chat.pastConversations.subtitleHint") }}</span>
                </span>
            </template>
            <template #actions>
                <BaseLink :href="$startChatUrl" :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }" class="base-btn-primary base-btn-with-icon base-btn-icon-start">
                    <i class="bi bi-plus-lg text-sm" aria-hidden="true"></i>
                    {{ t("chat.pastConversations.newChat") }}
                </BaseLink>
            </template>
        </BasePageHeader>

        <BaseNotification v-if="deleteError" theme="error" :text="deleteError" @dismiss="dismissDeleteError" />

        <!-- Insight card (hidden while a search is active) -->
        <InsightCardLayout v-if="insight && !searchPanelHasSearched">
            <div class="flex items-center justify-between mb-1">
                <div class="flex items-center gap-button-icon-only-sm">
                    <span class="inline-block size-1.75 rounded-full bg-insight-accent-dot" aria-hidden="true"></span>
                    <span class="scaffold-body-small text-ink-muted">{{ t("chat.pastConversations.insightLabel") }}</span>
                </div>
                <FeedbackButtons :value="insightFeedback" @select="handleInsightFeedback" @negative-feedback="handleInsightNegativeFeedback" />
            </div>

            <h3 class="display-small-serif text-ink">{{ insight.title || t("chat.pastConversations.insightDefaultTitle") }}</h3>
            <div class="border-t border-border my-2.5"></div>
            <p class="body-small text-ink-muted fs-mask">{{ insight.content }}</p>
            <div class="mt-auto pt-5">
                <BaseButton theme="neutral" @click="handleInsightClick">{{ t("chat.pastConversations.diveDeeper") }}</BaseButton>
            </div>
        </InsightCardLayout>

        <!-- Conversation search -->
        <ConversationSearchPanel
            v-if="conversation_search_enabled"
            ref="searchPanel"
            :document-upload-enabled="props.document_upload_enabled"
            :document-creator-enabled="props.document_creator_enabled"
            :artifacts-enabled="props.artifacts_enabled"
            :search-owner="props.search_owner" />

        <!-- Loading state -->
        <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ loadingAnnouncement }}</span>
        <div v-if="!searchPanelHasSearched && loading" class="flex justify-center items-center p-10">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-ink-muted"></div>
        </div>

        <!-- Empty -->
        <div
            v-else-if="!searchPanelHasSearched && pastConversations.length === 0"
            class="bg-surface-raised border-2 border-border rounded-lgr px-10 py-10 text-center flex flex-col items-center gap-3">
            <div class="w-12 h-12 rounded-full bg-surface-sunken inline-flex items-center justify-center text-ink-muted" aria-hidden="true">
                <i class="bi bi-chat-left-text text-lg" aria-hidden="true"></i>
            </div>
            <div class="display-serif text-ink">{{ t("chat.pastConversations.emptyTitle") }}</div>
            <p class="body-small text-ink-muted max-w-90">{{ t("chat.pastConversations.emptyDescription") }}</p>
            <BaseLink :href="$startChatUrl" :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }" class="mt-2 base-btn-primary base-btn-with-icon base-btn-icon-start">
                <i class="bi bi-plus-lg text-sm" aria-hidden="true"></i>
                {{ t("chat.pastConversations.startChat") }}
            </BaseLink>
        </div>

        <div v-else-if="!searchPanelHasSearched" class="flex flex-col gap-2 md:gap-0 md:bg-surface-raised md:border md:border-border md:rounded-lgr md:p-4">
            <div v-for="group in groupedConversations" :key="group.label" class="pb-6">
                <div class="px-1 py-2 md:px-5 md:py-2.5 flex items-baseline gap-2 md:sticky md:top-0 md:bg-surface-raised/85 md:backdrop-blur md:z-10">
                    <div class="scaffold-body-small text-ink-muted uppercase tracking-widest">{{ group.label }}</div>
                    <div class="scaffold-body-small text-ink-muted hidden md:block">{{ group.sessions.length }}</div>
                </div>
                <div class="bg-surface-raised rounded-xl border border-border overflow-hidden md:bg-transparent md:rounded-none md:border-0 md:overflow-visible md:px-4">
                    <template v-for="(conversation, idx) in group.sessions" :key="conversation.hashid">
                        <div v-if="idx > 0" class="conversation-divider border-t border-border mx-4 md:mx-5"></div>
                        <PastConversationRowV2
                            :deletion-disabled="past_chats_deletion_disabled"
                            :data="conversation"
                            @rename="renamePastConversation"
                            @delete="handleDeletePastConversation" />
                    </template>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="!searchPanelHasSearched && pagination && pagination.total_pages > 1" class="flex justify-center items-center gap-4 py-2">
            <BaseButton v-if="pagination.current_page > 2" :disabled="loading" theme="primary" @click="loadPage(1)">{{ t("chat.pastConversations.mostRecent") }}</BaseButton>
            <BaseButton :disabled="!pagination.has_previous || loading" theme="primary" @click="loadPage(pagination.current_page - 1)">
                <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> {{ t("chat.pastConversations.newer") }}
            </BaseButton>
            <span class="body-small text-ink-muted">{{ t("chat.pastConversations.pageOf", { current: pagination.current_page, total: pagination.total_pages }) }}</span>
            <BaseButton :disabled="!pagination.has_next || loading" theme="primary" @click="loadPage(pagination.current_page + 1)">
                {{ t("chat.pastConversations.older") }} <i class="bi bi-arrow-right ml-1" aria-hidden="true"></i>
            </BaseButton>
        </div>
    </BaseLayout>

    <ConfirmationDialog
        v-model:open="isConfirmDeleteDialogOpen"
        :title="t('chat.pastConversations.deleteDialogTitle')"
        :text="t('chat.pastConversations.deleteDialogText')"
        :confirm-label="t('common.delete')"
        @confirm="confirmDeletePastConversation" />
</template>

<script>
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup>
import { getDateBucket } from "/js/dateUtils";
import { Head, router } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import ConversationSearchPanel from "~vue/components/ConversationSearchPanel.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import PastConversationRowV2 from "~vue/components/PastConversationRowV2.vue";
import { useDeletePastConversation } from "~vue/composables/useDeletePastConversation";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import { useRenamePastConversation } from "~vue/composables/useRenamePastConversation";
import FeedbackButtons from "~vue/Labs/FeedbackButtons.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    past_conversations: { type: Array, required: true },
    past_chats_deletion_disabled: { type: Boolean, required: true },
    conversation_search_enabled: { type: Boolean, default: false },
    document_upload_enabled: { type: Boolean, default: false },
    document_creator_enabled: { type: Boolean, default: false },
    artifacts_enabled: { type: Boolean, default: false },
    search_owner: { type: String, default: "" },
    pagination: { type: Object, default: () => ({}) },
    insight: { type: Object, default: null },
});

onMounted(() => {
    if (props.insight) {
        logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_SHOWN, { card_type: PAST_CONVERSATIONS_INSIGHT_CARD_TYPE, title: props.insight.title });
    }
});
const pastConversations = computed(() => props.past_conversations);
const pagination = computed(() => props.pagination);
const insight = computed(() => props.insight);
const loading = ref(false);
const PAST_CONVERSATIONS_INSIGHT_CARD_TYPE = "past_conversations_insight";
const totalCount = computed(() => props.pagination?.total ?? props.past_conversations.length);

// Matches the literal bucket names returned by getDateBucket (/js/dateUtils).
const BUCKET_ORDER = ["Today", "Yesterday", "This week", "This month", "Earlier"];

// A literal switch, not a key lookup table, so the vue-i18n ESLint catalogue check can see
// these chat.pastConversations.bucket.* keys are used.
function translateBucketLabel(bucket) {
    switch (bucket) {
        case "Today":
            return t("chat.pastConversations.bucket.today");
        case "Yesterday":
            return t("chat.pastConversations.bucket.yesterday");
        case "This week":
            return t("chat.pastConversations.bucket.thisWeek");
        case "This month":
            return t("chat.pastConversations.bucket.thisMonth");
        default:
            return t("chat.pastConversations.bucket.earlier");
    }
}

const groupedConversations = computed(() => {
    const buckets = {};

    for (const conv of pastConversations.value) {
        const bucket = getDateBucket(conv.updated_at);
        if (!buckets[bucket]) {
            buckets[bucket] = [];
        }
        buckets[bucket].push(conv);
    }

    return BUCKET_ORDER.filter((label) => buckets[label]).map((label) => ({ label: translateBucketLabel(label), sessions: buckets[label] }));
});

const searchPanel = ref(null);
const searchPanelHasSearched = computed(() => searchPanel.value?.hasSearched ?? false);
const loadingAnnouncement = useLoadingAnnouncement(
    () => !searchPanelHasSearched.value && loading.value,
    () => t("chat.pastConversations.loadingAnnouncement"),
);

const insightFeedback = ref(null);

const handleInsightFeedback = (value) => {
    insightFeedback.value = value;
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, {
        card_type: PAST_CONVERSATIONS_INSIGHT_CARD_TYPE,
        action: value === 1 ? "thumbs_up" : "thumbs_down",
        location: HOMEPAGE_CARD_LOCATION.CHAT_HISTORY,
    });
};

const handleInsightNegativeFeedback = (payload) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, {
        card_type: PAST_CONVERSATIONS_INSIGHT_CARD_TYPE,
        action: "negative_feedback",
        comment: payload.comment,
        issues: payload.issues,
        location: HOMEPAGE_CARD_LOCATION.CHAT_HISTORY,
    });
};

const handleInsightClick = () => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: PAST_CONVERSATIONS_INSIGHT_CARD_TYPE, location: HOMEPAGE_CARD_LOCATION.CHAT_HISTORY });
    openUrl(props.insight.url, {
        queryParams: { chat_source: CHATSOURCE.PAST_CONVERSATIONS_INSIGHT },
    });
};

const loadPage = async (page) => {
    if (loading.value) {
        return;
    }
    loading.value = true;

    router.get(
        globalThis.location.pathname,
        { page },
        {
            preserveState: false,
            preserveScroll: false,
            only: ["past_conversations", "pagination"],
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};

const { renamePastConversation } = useRenamePastConversation({
    onResponseCallback: () => router.reload({ only: ["past_conversations", "pagination"] }),
    source: "Past conversation page",
});

const { deletePastConversation, deleteError, dismissDeleteError } = useDeletePastConversation({
    onResponseCallback: () => router.reload({ only: ["past_conversations", "pagination"] }),
    source: "Past conversation page",
});

const isConfirmDeleteDialogOpen = ref(false);
const pendingDeletion = ref(null);

const handleDeletePastConversation = ({ kind, hashid, uuid }) => {
    if (props.past_chats_deletion_disabled) {
        return;
    }
    pendingDeletion.value = { kind, hashid, uuid };
    isConfirmDeleteDialogOpen.value = true;
};

const confirmDeletePastConversation = () => {
    if (!pendingDeletion.value) {
        return;
    }
    deletePastConversation(pendingDeletion.value);
    pendingDeletion.value = null;
};
</script>

<style scoped>
/* Hide divider lines adjacent to hovered row */
.conversation-divider:has(+ a:hover),
a:hover + .conversation-divider {
    opacity: 0;
}
</style>
